import React from 'react';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';

export interface TicketData {
  tipo: 'ENTRADA' | 'SALIDA';
  nombreEmpresa: string;
  nitEmpresa?: string;
  direccion?: string;
  numeroTicket: string;
  placa: string;
  tipoVehiculo: string;
  horaEntrada: string;
  operador: string;
  // Campos Salida
  horaSalida?: string;
  duracion?: string;
  tarifa?: number;
  total?: number;
  metodoPago?: string;
  // Factura Electrónica
  cufe?: string;
  qrInvoice?: string;
  // Extras
  infoAdicional?: string;
  facturaElectronica?: string;
}

interface TicketPrintTemplateProps {
  data: TicketData;
  width: '50mm' | '80mm';
}

export const TicketPrintTemplate = React.forwardRef<HTMLDivElement, TicketPrintTemplateProps>(
  ({ data, width }, ref) => {
    // Estilos base para impresoras térmicas
    const containerStyle: React.CSSProperties = {
      width: width === '50mm' ? '180px' : '280px', // Ajuste a píxeles aproximados para térmicas
      padding: width === '50mm' ? '2px' : '10px',
      fontFamily: 'monospace',
      fontSize: width === '50mm' ? '10px' : '12px',
      lineHeight: '1.2',
      textAlign: 'center',
      backgroundColor: 'white',
      color: 'black',
      margin: '0 auto'
    };

    const headerStyle: React.CSSProperties = {
        fontWeight: 'bold',
        fontSize: '14px',
        marginBottom: '5px'
    };

    const separatorStyle: React.CSSProperties = {
        borderTop: '1px dashed black',
        margin: '5px 0',
        width: '100%'
    };

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '3px'
    };

    return (
      <div ref={ref} style={containerStyle}>
        <div style={headerStyle}>{data.nombreEmpresa}</div>
        {data.nitEmpresa && <div>NIT: {data.nitEmpresa}</div>}
        {data.direccion && <div>{data.direccion}</div>}
        
        <div style={separatorStyle} />
        
        <div style={{ fontWeight: 'bold', fontSize: '16px', margin: '5px 0' }}>
            {data.tipo === 'SALIDA' ? 'FACTURA DE VENTA' : 'ENTRADA'}
        </div>
        {data.infoAdicional && (
             <div style={{ fontWeight: 'bold', fontSize: '14px', margin: '2px 0', border: '1px solid black', padding: '2px' }}>
                {data.infoAdicional}
             </div>
        )}
        
        <div style={rowStyle}>
            <span>{data.tipo === 'SALIDA' ? 'Factura #:' : 'Ticket #:'}</span>
            <strong>{data.numeroTicket}</strong>
        </div>
        <div style={rowStyle}>
            <span>Placa:</span>
            <strong>{data.placa}</strong>
        </div>
        <div style={rowStyle}>
            <span>Tipo:</span>
            <span>{data.tipoVehiculo}</span>
        </div>
        
        {/* Fechas */}
        <div style={rowStyle}>
            <span>Entrada:</span>
            <span>{new Date(data.horaEntrada).toLocaleString()}</span>
        </div>
        {data.horaSalida && (
            <div style={rowStyle}>
                <span>Salida:</span>
                <span>{new Date(data.horaSalida).toLocaleString()}</span>
            </div>
        )}
        {data.duracion && (
            <div style={rowStyle}>
                <span>Tiempo:</span>
                <span>{data.duracion}</span>
            </div>
        )}

        <div style={rowStyle}>
            <span>Operador:</span>
            <span>{data.operador || 'Sistema'}</span>
        </div>

        {/* Sección de Cobro (Sólo Salida) */}
        {data.tipo === 'SALIDA' && (
            <>
                <div style={separatorStyle} />
                 <div style={rowStyle}>
                    <span>Tarifa Base:</span>
                    <span>${data.tarifa?.toLocaleString()}</span>
                </div>
                 {/* Descuentos, etc si hubiera */}
                 <div style={{...rowStyle, fontSize: '14px', marginTop: '5px'}}>
                    <strong>TOTAL:</strong>
                    <strong>${data.total?.toLocaleString()}</strong>
                </div>
                <div style={rowStyle}>
                    <span>Método Pago:</span>
                    <span>{data.metodoPago}</span>
                </div>
            </>
        )}

        {/* Facturación Electrónica */}
        {data.cufe && (
             <>
                <div style={separatorStyle} />
                <div style={{ fontSize: '9px', wordBreak: 'break-all', textAlign: 'left' }}>
                    <strong>CUFE:</strong>
                    <div>{data.cufe}</div>
                </div>
             </>
        )}

        <div style={separatorStyle} />
        
        {/* Código de Barras para ENTRADA, QR para SALIDA/Factura */}
        {data.tipo === 'ENTRADA' ? (
            <div style={{ margin: '10px 0', display: 'flex', justifyContent: 'center' }}>
                <Barcode 
                    value={data.placa} 
                    width={width === '50mm' ? 1.2 : 1.8}
                    height={width === '50mm' ? 30 : 50}
                    fontSize={width === '50mm' ? 10 : 12}
                    displayValue={true}
                    format="CODE128"
                />
            </div>
        ) : data.qrInvoice ? (
            <div style={{ margin: '10px 0', display: 'flex', justifyContent: 'center' }}>
                <QRCode value={data.qrInvoice} size={width === '50mm' ? 80 : 140} />
            </div>
        ) : null}
        
        <div style={{ fontSize: '10px', marginTop: '5px' }}>
            {data.tipo === 'SALIDA' ? '¡Gracias por su visita!' : 'Conserve este ticket para la salida.'}
            <br/>PARQIUM System
        </div>
      </div>
    );
  }
);
